import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useArticulosBusqueda } from '@/hooks/useOrdenesServicio'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import type { ArticuloOpcion } from '@/types'

interface Props {
  dark: boolean
  onSelect: (art: ArticuloOpcion) => void
}

/** Buscador de artículos con debounce (Fabric real) + dropdown de resultados. */
export function ArticuloPicker({ dark, onSelect }: Props) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const id = setTimeout(() => setDebounced(q.trim()), 300)
    return () => clearTimeout(id)
  }, [q])

  // Cerrar el dropdown al hacer clic fuera.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const { data: articulos = [], isFetching } = useArticulosBusqueda(debounced, open)

  const inputCls = `w-full min-h-[40px] px-3 rounded-lg border text-[13px] ${
    dark ? 'bg-white/5 border-white/15 text-slate-100 placeholder-slate-500' : 'bg-white border-slate-300 text-slate-800'
  }`
  const menuCls = `absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-lg border shadow-lg ${
    dark ? 'bg-slate-800 border-white/15' : 'bg-white border-slate-200'
  }`
  const itemCls = `w-full text-left px-3 py-2 cursor-pointer transition-colors ${
    dark ? 'hover:bg-white/10' : 'hover:bg-slate-50'
  }`
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'
  const mainTxt = dark ? 'text-slate-100' : 'text-slate-800'

  const pick = (art: ArticuloOpcion) => {
    onSelect(art)
    setQ('')
    setDebounced('')
    setOpen(false)
  }

  return (
    <div className="relative" ref={boxRef}>
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        placeholder={t('ordenesServicio.form.buscarArticulo')}
        className={inputCls}
      />
      {open && (
        <div className={menuCls}>
          {isFetching ? (
            <p className={`px-3 py-2 text-[12px] ${mutedTxt}`}>…</p>
          ) : articulos.length === 0 ? (
            <p className={`px-3 py-2 text-[12px] ${mutedTxt}`}>{t('ordenesServicio.form.sinArticulos')}</p>
          ) : (
            articulos.map((a, i) => (
              <button key={`${a.empresa}-${a.itemid}-${i}`} type="button" className={itemCls} onClick={() => pick(a)}>
                <span className="flex items-center gap-2">
                  <span className={`text-[13px] font-semibold ${mainTxt}`}>{fmtArt(a.itemid)}</span>
                  {a.linea === 'ARMAF' && (
                    <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500">ARMAF</span>
                  )}
                </span>
                <span className={`block text-[12px] truncate ${mutedTxt}`}>{a.descripcion || '—'}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
